import type { BadgeTone } from '../../components/ui'
import type { BookingSummary } from '../../types/storefront'
import { formatLongDate, formatSlotTime, slotDate } from '../../utils/datetime'

/** frezka's booking statuses, in the customer's words. */
const STATUS_LABEL: Record<string, string> = {
  pending: 'Awaiting confirmation',
  confirmed: 'Confirmed',
  check_in: 'Checked in',
  // The dashboard checkout step takes no payment; `completed` is the finished visit.
  checkout: 'Awaiting payment',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

const STATUS_TONE: Record<string, BadgeTone> = {
  pending: 'warning',
  confirmed: 'primary',
  check_in: 'primary',
  checkout: 'warning',
  completed: 'success',
  cancelled: 'error',
}

export function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status
}

export function statusTone(status: string): BadgeTone {
  return STATUS_TONE[status] ?? 'neutral'
}

/** "Thursday, September 15, 2026 · 10:30" in the salon's own clock. */
export function bookingWhen(booking: BookingSummary): string {
  const date = slotDate(booking.starts_at)
  if (!date) return ''
  return `${formatLongDate(date)} · ${formatSlotTime(booking.starts_at)}`
}

/** Short form for list rows: "09/15/2026 · 10:30". */
export function bookingWhenShort(booking: BookingSummary): string {
  const date = slotDate(booking.starts_at)
  if (!date) return ''
  const [year, month, day] = date.split('-')
  return `${month}/${day}/${year} · ${formatSlotTime(booking.starts_at)}`
}

/**
 * The vendor's cancellation rule, stated before the customer commits to anything.
 * Null when the salon set no notice period — saying nothing beats inventing a rule.
 */
export function cancelPolicyNote(cutoffHours: number | null): string | null {
  if (cutoffHours === null || cutoffHours <= 0) return null
  return `This salon needs at least ${cutoffHours} hour${cutoffHours === 1 ? '' : 's'} of notice to cancel an appointment.`
}
